import React, { useEffect, useState } from 'react';

import { useIsMutating } from '@tanstack/react-query';

import { injectIntl, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { Dropdown, Menu } from 'antd';
import { FolderOutlined, DownloadOutlined } from '@ant-design/icons';

import RibbonButton from 'common/components/button/RibbonButton';
import CreateFolder from '../../controls/folders/CreateFolder';
import DeleteFolders from '../../controls/folders/DeleteFolders';
import CopyFolder from '../../controls/folders/CopyFolder';
import MoveFolder from '../../controls/folders/MoveFolder';
import EditFolder from '../../controls/folders/EditFolder';
import BulkEditProduct from '../../controls/product/BulkEditProduct';
import CopyFolderModal from '../../controls/folders/CopyFolderModal';
import MoveFolderModal from '../../controls/folders/MoveFolderModal';
import ExportProductPropertiesModal from '../../modal/bulk-edit-product/ExportProductPropertiesModal';
import ImportProductBulkEditModal from '../../modal/bulk-edit-product/ImportProductBulkEditModal';
import CreateFolderModal from '../../controls/folders/CreateFolderModal';
import { CustomNotification } from 'common/components';
import FolderEditModal from 'pages/folders/components/modal/FolderEditModal';

import { Can } from 'context/Can';

import { useCheckPermissions } from 'hooks/useCheckPermissions';
import useCheckIsFolderOwner from 'hooks/useCheckIsFolderOwner';
import { useFolderByLevel } from 'hooks/useFolderByLevel';
import {
  usePermissionEditSharedProduct,
  useCheckIsSuperAdmin,
  useGlobalModal,
  useFetch,
} from 'hooks';
import { useDispatchReloadPage } from 'hooks/useReloadPage';
import {
  EXPORT_PRODUCT_PROPERTIES,
  useExportProductProperty,
} from '../../modal/bulk-edit-product/hook';

import { sleep } from 'utils/delay';

import messagesFolder from 'i18n/messages/folder';
import Messages from 'i18n/messages/home';
import messagesProduct from 'i18n/messages/product';

import {
  ABILITY_ACTION,
  ABILITY_SUBJECT,
  PERMISSION_EDIT_PRODUCTS,
} from 'static/Permission';
import { ROOT_FOLDER } from 'pages/folders/controllers/constants';
import { IMPORT_PRODUCT_FOR_BULK_EDITING } from 'services/product/endpoints';

import './FolderSection.less';

const { SubMenu } = Menu;

const FolderSection = (props) => {
  const { selectedItems } = props;

  const { pathname } = useLocation();

  const intl = useIntl();

  const [copyFolderModalVisible, setCopyFolderVisible] = useState(false);
  const [moveFolderModalVisible, setMoveFolderVisible] = useState(false);
  const [editFolderModalVisible, setEditFolderVisible] = useState(false);

  const { MODAL_NAMES, checkModalVisible, closeModal } = useGlobalModal();

  const { data: response, run: runFetchFolderShortContent } = useFetch();

  const reloadPage = useDispatchReloadPage();

  const visibleModalHook = useState(false);
  const [visible, setVisible] = visibleModalHook;

  const exportProductPropertyMutation = useExportProductProperty();

  const isExportProductPropertyLoading = useIsMutating({
    mutationKey: EXPORT_PRODUCT_PROPERTIES,
  });

  const onCreateFolderSuccess = () => {
    CustomNotification.success(
      intl.formatMessage(messagesFolder.createFolderSuccess)
    );
    reloadPage();
  };

  const isExportProductModalOpen = checkModalVisible(
    MODAL_NAMES.EXPORT_PROPERTY_MODAL
  );

  const isImportProductModalOpen = checkModalVisible(
    MODAL_NAMES.IMPORT_PROPERTY_MODAL
  );

  const checkDisabledBulkEditProductBtn = () => {
    const isAllowActionsPim =
      isSuperAdmin ||
      hasPermissionEditProducts ||
      hasPermissionEditSharedProduct;

    return !isAllowActionsPim;
  };

  const checkDisableExportBulkProduct = () => {
    const isFolderContainProduct =
      selectedFolders.length > 0 &&
      selectedFolders?.every((folderItem) => {
        return !!folderItem.productCount;
      });
    const isProductsSelected = selectedProductIds.length > 0;

    const isValidItemSelected = isFolderContainProduct || isProductsSelected;

    //* checking for enable cases is easier
    const shouldEnable = isValidItemSelected;

    return !shouldEnable;
  };

  const checkDisableCopyFolder = () => {
    //* only support coping single folder
    if (selectedFolders?.length !== 1) return true;

    return false;
  };

  const checkDisableMoveFolder = () => {
    const disabled = selectedFolders.some((folder) => {
      const isSharedFolder = !folder.isOwner;
      const isEditable = folder.editable;

      //* disable when there is a folder that shared and editable = false
      return isSharedFolder && !isEditable;
    });

    return disabled;
  };

  const closeCopyModal = () => {
    setCopyFolderVisible(false);
  };

  const closeMoveModal = () => {
    setMoveFolderVisible(false);
  };

  const getSelectedFolders = () => {
    return selectedItems.filter((item) => item.type.toLowerCase() === 'folder');
  };
  const getSelectedFolderIds = () => {
    return selectedFolders.map((item) => item.id);
  };
  const getSelectedProductIds = () => {
    return selectedItems.reduce((accumulator, currentItem) => {
      const isProduct = currentItem.type.toLowerCase() === 'product';

      if (!isProduct) return accumulator;

      return [...accumulator, currentItem.id];
    }, []);
  };

  const onSaveEditModalSuccess = async () => {
    await sleep(200);
    setEditFolderVisible(false);
    reloadPage({ clearSelection: true });
  };

  const selectedFolders = getSelectedFolders();
  const selectedFolder = selectedItems?.length === 1 && selectedFolders[0];
  const selectedFolderIds = getSelectedFolderIds();
  const selectedProductIds = getSelectedProductIds();

  const isOwner = useCheckIsFolderOwner();
  const isSuperAdmin = useCheckIsSuperAdmin();
  const hasPermissionEditSharedProduct = usePermissionEditSharedProduct();
  const hasPermissionEditProducts = useCheckPermissions(
    PERMISSION_EDIT_PRODUCTS
  );

  const { currentBreadcrumb } = useFolderByLevel();

  const destinationFolder = selectedFolder
    ? selectedFolder
    : currentBreadcrumb?.id !== ROOT_FOLDER
    ? currentBreadcrumb
    : null;

  const isDisabledBulkEditProductBtn = checkDisabledBulkEditProductBtn();
  const isDisabledExportBulkEditProduct = checkDisableExportBulkProduct();
  const isDisabledCopyFolder = checkDisableCopyFolder();
  const isDisabledMoveFolder = checkDisableMoveFolder();

  const onExportProductProperty = (exportProperties, allPopulated) => {
    exportProductPropertyMutation.mutate({
      productIds: selectedProductIds,
      folderIds: selectedFolderIds,
      selectedColumns: exportProperties,
      allPopulated,
    });
  };

  const menuOptions = (
    <Menu className='folder-section__menu'>
      <Menu.Item key='editFolder' style={{ backgroundColor: 'inherit' }}>
        <Can I={ABILITY_ACTION.EDIT} a={ABILITY_SUBJECT.FOLDER} passThrough>
          {(allowed) => (
            <EditFolder
              disabled={!allowed}
              selectedFolders={selectedFolders}
              setEditFolderVisible={setEditFolderVisible}
              runFetchFolderShortContent={runFetchFolderShortContent}
            />
          )}
        </Can>
      </Menu.Item>

      <Menu.Item key='favorite' style={{ backgroundColor: 'inherit' }}>
        <Can I={ABILITY_ACTION.CREATE} a={ABILITY_SUBJECT.FOLDER} passThrough>
          {(allowed) => {
            return (
              <CreateFolder
                setVisible={setVisible}
                disabled={!allowed}
                pathname={pathname}
                parentId={destinationFolder?.id}
                destinationFolder={destinationFolder}
              />
            );
          }}
        </Can>
      </Menu.Item>

      <Menu.Item key='addToFolder' style={{ backgroundColor: 'inherit' }}>
        <Can I={ABILITY_ACTION.DELETE} a={ABILITY_SUBJECT.FOLDER} passThrough>
          {(allowed) => (
            <DeleteFolders
              disabled={!allowed}
              pathname={pathname}
              isOwner={isOwner}
              selectedItems={selectedItems}
            />
          )}
        </Can>
      </Menu.Item>

      <Menu.Item key='copyFolder' style={{ backgroundColor: 'inherit' }}>
        <Can I={ABILITY_ACTION.COPY} a={ABILITY_SUBJECT.FOLDER} passThrough>
          {(allowed) => (
            <CopyFolder
              disabled={!allowed || isDisabledCopyFolder}
              pathname={pathname}
              selectedFolders={selectedFolders}
              onClick={() => setCopyFolderVisible(true)}
            />
          )}
        </Can>
      </Menu.Item>
      <Menu.Item key='moveFolder' style={{ backgroundColor: 'inherit' }}>
        <Can I={ABILITY_ACTION.COPY} a={ABILITY_SUBJECT.FOLDER} passThrough>
          {(allowed) => (
            <MoveFolder
              disabled={!allowed || isDisabledMoveFolder}
              pathname={pathname}
              selectedItems={selectedItems}
              onClick={() => setMoveFolderVisible(true)}
            />
          )}
        </Can>
      </Menu.Item>
      <SubMenu
        key={'bulk-edit-product'}
        style={{ backgroundColor: 'inherit' }}
        disabled={isDisabledBulkEditProductBtn}
        title={
          <RibbonButton
            size='small'
            className='button-item-dropdown'
            icon={<DownloadOutlined />}
            disabled={isDisabledBulkEditProductBtn}
            label={messagesProduct.bulkEditProduct}
          />
        }
      >
        <BulkEditProduct
          disabled={isDisabledBulkEditProductBtn}
          disabledExport={isDisabledExportBulkEditProduct}
          selectedFolders={selectedFolderIds}
          selectedProducts={selectedProductIds}
        />
      </SubMenu>
    </Menu>
  );
  return (
    <>
      <Dropdown overlay={menuOptions} placement='bottomLeft' arrow>
        <RibbonButton
          icon={<FolderOutlined />}
          label={Messages.folderActions}
        />
      </Dropdown>
      <CopyFolderModal
        visible={copyFolderModalVisible}
        closeModal={closeCopyModal}
        selectedFolder={selectedFolder}
      />
      <MoveFolderModal
        visible={moveFolderModalVisible}
        closeModal={closeMoveModal}
        selectedItems={selectedItems}
        currentFolderId={currentBreadcrumb?.id}
      />
      {isExportProductModalOpen && (
        <ExportProductPropertiesModal
          title={intl.formatMessage(messagesProduct.exportProperties)}
          onExport={onExportProductProperty}
          visible={isExportProductModalOpen}
          loading={isExportProductPropertyLoading}
          closeModal={() => closeModal(MODAL_NAMES.EXPORT_PROPERTY_MODAL)}
        />
      )}
      {isImportProductModalOpen && (
        <ImportProductBulkEditModal
          title={intl.formatMessage(messagesProduct.importProductBulkEditTitle)}
          apiUrl={IMPORT_PRODUCT_FOR_BULK_EDITING}
          visible={isImportProductModalOpen}
          closeModal={() => closeModal(MODAL_NAMES.IMPORT_PROPERTY_MODAL)}
        />
      )}
      {visible && (
        <CreateFolderModal
          visibleHook={visibleModalHook}
          successCallback={onCreateFolderSuccess}
          parentId={destinationFolder?.id}
          skipNotification={true}
        />
      )}
      {editFolderModalVisible ? (
        <FolderEditModal
          visible={editFolderModalVisible}
          onCancel={() => setEditFolderVisible(false)}
          onSaveSuccess={onSaveEditModalSuccess}
          folderInfo={response?.data}
          idSelected={selectedFolders?.[0]?.id}
        />
      ) : null}
    </>
  );
};

export default injectIntl(FolderSection);
